/** Geteilte Konstanten: Status, Prioritäten, Rechte, Labels. */

export const TASK_STATUSES = ['open', 'in_progress', 'review', 'done', 'cancelled'] as const;
export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  review: 'Review',
  done: 'Erledigt',
  cancelled: 'Abgebrochen',
};

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Niedrig',
  normal: 'Normal',
  high: 'Hoch',
  urgent: 'Dringend',
};

/** Reihenfolge der Badges/Farben: niedrig → dringend. */
export const PRIORITY_RANK: Record<TaskPriority, number> = {
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4,
};

export function statusLabel(status: string): string {
  return isTaskStatus(status) ? STATUS_LABELS[status] : status;
}

export function priorityLabel(priority: string): string {
  return isTaskPriority(priority) ? PRIORITY_LABELS[priority] : priority;
}

export function isTaskStatus(value: string): value is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(value);
}

export function isTaskPriority(value: string): value is TaskPriority {
  return (TASK_PRIORITIES as readonly string[]).includes(value);
}

/** CSS-Klasse für den Status-Punkt (nur bekannte Status). */
export function statusClass(status: string): string {
  return isTaskStatus(status) ? `status-${status}` : 'status-unknown';
}

export function priorityClass(priority: string): string {
  return isTaskPriority(priority) ? `prio-${priority}` : 'prio-unknown';
}

// ---------------------------------------------------------------------------
// Rechte
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  TASK_VIEW: 'task.view',
  TASK_VIEW_ALL: 'task.view_all',
  TASK_CREATE: 'task.create',
  TASK_UPDATE: 'task.update',
  TASK_DELETE: 'task.delete',
  TASK_ASSIGN: 'task.assign',
  HISTORY_VIEW: 'history.view',
  USER_MANAGE: 'user.manage',
  ROLE_MANAGE: 'role.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export function hasPermission(permissions: readonly string[], permission: Permission): boolean {
  return permissions.includes(permission);
}

// ---------------------------------------------------------------------------
// Historie
// ---------------------------------------------------------------------------

export const ACTION_LABELS: Record<string, string> = {
  created: 'angelegt',
  updated: 'geändert',
  assigned: 'zugewiesen',
  comment: 'Kommentar',
  deleted: 'gelöscht',
};

export const FIELD_LABELS: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  status: 'Status',
  priority: 'Priorität',
  assigneeId: 'Zuweisung',
  dueAt: 'Fälligkeit',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function fieldLabel(field: string | null): string | null {
  if (field === null) return null;
  return FIELD_LABELS[field] ?? field;
}
